'use client';

import { useState, useMemo, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Image from 'next/image';
import { Collapse, Button, Input } from 'rizzui';
import {
  PiMagnifyingGlass,
  PiBuildings,
  PiTruck,
  PiMapPin,
  PiUsers,
} from 'react-icons/pi';
import './faq-redesign.css';
import { FaqClosingCta } from './faq-closing-cta';
import { SubPageShell, CornerArrow } from '../components/sub-page-sections';

interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

interface FaqCategory {
  number: string;
  title: string;
  description: string;
  items: FaqItem[];
}

const FAQ_CATEGORIES: FaqCategory[] = [
  {
    number: '01',
    title: 'Company & Coverage',
    description:
      'Essential information about Ship Smart Solutions, our operating model, and service area.',
    items: [
      {
        id: 'faq-1-1',
        question: 'What is Ship Smart Solutions?',
        answer:
          'Ship Smart Solutions is an asset-based trucking company and logistics provider headquartered in Naperville, Illinois. We provide transportation and logistics solutions for shippers across the contiguous United States.',
      },
      {
        id: 'faq-1-2',
        question: 'Is Ship Smart Solutions asset-based or a freight broker?',
        answer:
          'We are asset-based and operate our own trucking capacity. Our owned fleet forms the foundation of our service, while a vetted carrier network supports overflow, specialized, and off-network lanes.',
      },
      {
        id: 'faq-1-3',
        question: 'Where does Ship Smart Solutions provide service?',
        answer:
          'We provide freight coverage across all 48 contiguous states from our central Naperville, Illinois hub, including regional Midwest lanes and coast-to-coast full-truckload transportation.',
      },
      {
        id: 'faq-1-4',
        question: 'Which industries do you serve?',
        answer:
          'We support retail, manufacturing, food and beverage, consumer goods, e-commerce, construction, and industrial customers with transportation programs built around their freight requirements.',
      },
    ],
  },
  {
    number: '02',
    title: 'Freight Services & Equipment',
    description:
      'Information about our transportation modes, equipment, and ongoing logistics programs.',
    items: [
      {
        id: 'faq-2-1',
        question: 'What freight services do you offer?',
        answer:
          'We provide dry van, reefer, flatbed and open-deck, heavy-haul, expedited, dedicated capacity, managed transportation, warehousing, and distribution solutions.',
      },
      {
        id: 'faq-2-2',
        question: 'Do you own your dry van equipment?',
        answer:
          'Yes. Ship Smart Solutions operates its own dedicated 53-foot dry van fleet for full-truckload freight. Vetted carriers are used when additional or specialized capacity is required.',
      },
      {
        id: 'faq-2-3',
        question: 'What is the difference between dry van and reefer freight?',
        answer:
          'Dry van trailers are enclosed but not refrigerated and are used for general palletized freight. Reefer trailers provide temperature control for products that must remain within a specified temperature range.',
      },
      {
        id: 'faq-2-4',
        question: 'Can you support dedicated or recurring freight lanes?',
        answer:
          'Yes. Our dedicated capacity and managed transportation programs can assign consistent equipment and coordinated support to repeating inbound and outbound freight lanes.',
      },
    ],
  },
  {
    number: '03',
    title: 'Quotes, Shipping & Tracking',
    description:
      'Details on quoting, booking, transit updates, and managing active shipments.',
    items: [
      {
        id: 'faq-3-1',
        question: 'How do I request a freight quote?',
        answer:
          'You can request a quote by contacting our team via phone or submitting your load details online. We review lane requirements, equipment needs, and scheduling to provide prompt rates.',
      },
      {
        id: 'faq-3-2',
        question: 'How does shipment tracking work?',
        answer:
          'We monitor active loads with real-time tracking integration, giving shippers visibility from pickup through dispatch, transit milestones, and final delivery confirmation.',
      },
      {
        id: 'faq-3-3',
        question: 'What information is needed to book a load?',
        answer:
          'To book a shipment, we need pickup and delivery locations, ready time, required delivery window, freight weight and dimensions, equipment type, and any special handling instructions.',
      },
      {
        id: 'faq-3-4',
        question: 'What happens if a delivery timeline changes?',
        answer:
          'Our operations team actively tracks transit milestones and communicates proactively if weather, traffic, or facility delays affect your delivery schedule.',
      },
    ],
  },
  {
    number: '04',
    title: 'Carrier Network & Support',
    description:
      'Guidance for partner carriers joining our network and working with our dispatch team.',
    items: [
      {
        id: 'faq-4-1',
        question: 'How can partner carriers join your network?',
        answer:
          'Carriers must submit an active MC/DOT authority, Certificate of Insurance naming Ship Smart Solutions, W-9, and completed carrier agreement through our setup portal.',
      },
      {
        id: 'faq-4-2',
        question: 'What insurance is required for network carriers?',
        answer:
          'Network carriers must maintain minimum $1,000,000 Auto Liability and $100,000 Cargo Insurance with appropriate endorsements prior to load dispatch.',
      },
      {
        id: 'faq-4-3',
        question: 'How are carrier payments handled?',
        answer:
          'We offer standard payment terms as well as quick-pay options upon receipt of clean Proof of Delivery (POD) and required accessorial documentation.',
      },
      {
        id: 'faq-4-4',
        question: 'Who do I contact for dispatch support?',
        answer:
          'Our dispatch and operations team is available 24/7/365 to assist carriers with load status, facility directions, rate confirmations, and transit updates.',
      },
    ],
  },
];

const TOPIC_CARDS = [
  {
    number: '01',
    title: 'Company & Coverage',
    description:
      'Learn about our asset-based operation, owned capacity, Midwest hub, and nationwide service coverage.',
    image: '/landing/assets/images/shipsmart/ship14.jpg',
    categoryId: 'category-01',
  },
  {
    number: '02',
    title: 'Freight Services',
    description:
      'Explore dry van, reefer, open-deck, expedited, dedicated, and managed transportation services.',
    image: '/landing/assets/images/shipsmart/ship3.jpg',
    categoryId: 'category-02',
  },
  {
    number: '03',
    title: 'Quotes & Tracking',
    description:
      'Find out how to request a freight quote, schedule a shipment, and follow your load through delivery.',
    image: '/landing/assets/images/shipsmart/gps-ship.webp',
    categoryId: 'category-03',
  },
  {
    number: '04',
    title: 'Carrier Network',
    description:
      'Review carrier requirements, onboarding steps, available freight, and network opportunities.',
    image: '/landing/assets/images/shipsmart/ship18.webp',
    categoryId: 'category-04',
  },
];

function FaqQuestion({
  item,
  number,
  defaultOpen,
}: {
  item: FaqItem;
  number: number;
  defaultOpen: boolean;
}) {
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!revision) return;
    const timer = window.setTimeout(() => ScrollTrigger.refresh(), 260);
    return () => window.clearTimeout(timer);
  }, [revision]);
  return (
    <Collapse
      defaultOpen={defaultOpen}
      duration={220}
      className="faq-question"
      panelClassName="faq-question-body"
      header={({ open, toggle }) => (
        <Button
          variant="text"
          className="faq-question-header"
          aria-expanded={Boolean(open)}
          aria-controls={`${item.id}-answer`}
          onClick={() => {
            toggle();
            setRevision((current) => current + 1);
          }}
        >
          <span className="faq-question-number">
            {String(number).padStart(2, '0')}
          </span>
          <span className="faq-question-title">{item.question}</span>
          <span className="faq-question-toggle" aria-hidden="true">
            {open ? '−' : '+'}
          </span>
        </Button>
      )}
    >
      <div id={`${item.id}-answer`}>
        <p>{item.answer}</p>
      </div>
    </Collapse>
  );
}

export function FaqPage({ locale = 'en' }: { locale?: string }) {
  const [searchQuery, setSearchQuery] = useState('');
  const query = searchQuery.trim().toLowerCase();
  const filteredCategories = useMemo(
    () =>
      FAQ_CATEGORIES.map((category) => ({
        ...category,
        items: category.items.filter(
          (item) =>
            !query ||
            `${item.question} ${item.answer}`.toLowerCase().includes(query)
        ),
      })).filter((category) => category.items.length),
    [query]
  );
  useEffect(() => {
    const frame = requestAnimationFrame(() => ScrollTrigger.refresh());
    return () => cancelAnimationFrame(frame);
  }, [query]);
  useEffect(() => {
    const root = document.querySelector<HTMLElement>('.faq-results');
    if (!root || query) return;
    gsap.registerPlugin(ScrollTrigger);
    const media = gsap.matchMedia();
    media.add('(prefers-reduced-motion: no-preference)', () => {
      root.querySelectorAll<HTMLElement>('.faq-category-grid').forEach((grid) => {
        gsap.from(grid, {
          y: 32,
          autoAlpha: 0,
          duration: 0.55,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: grid,
            start: 'top 90%',
            toggleActions: 'play none none none',
            once: true,
          },
        });
      });
      ScrollTrigger.refresh();
    });
    return () => media.revert();
  }, [query]);
  const topicIcons = [PiBuildings, PiTruck, PiMapPin, PiUsers];
  const scrollToCategory = (id: string) => {
    const element = document.getElementById(id);
    if (!element) return;
    const smoother = ScrollSmoother.get();
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    if (smoother)
      smoother.scrollTo(element, !reducedMotion, 'top 110px');
    else
      element.scrollIntoView({
        behavior: reducedMotion ? 'auto' : 'smooth',
        block: 'start',
      });
  };

  return (
    <SubPageShell
      rootClassName="faq-redesign-root"
      mainClassName="faq-redesign"
      seo={{ locale, section: 'company', slug: 'faq' }}
    >
      <section className="faq-hero faq-ribbons">
        <svg
          className="faq-hero-art"
          viewBox="0 0 1658 630"
          preserveAspectRatio="none"
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            <linearGradient id="faq-hero-soft" x1="0" x2="1" y1="1" y2="0">
              <stop stopColor="#e9f7ff" stopOpacity="0.25" />
              <stop offset="0.65" stopColor="#b9e2ff" stopOpacity="0.82" />
              <stop offset="1" stopColor="#93d0ff" stopOpacity="0.9" />
            </linearGradient>
            <linearGradient id="faq-hero-bright" x1="0" x2="1" y1="1" y2="0">
              <stop stopColor="#b8e6ff" stopOpacity="0.35" />
              <stop offset="1" stopColor="#39a9fa" stopOpacity="0.82" />
            </linearGradient>
          </defs>
          <path
            d="M 535 630 L 996 215 Q 1018 193 1045 193 H 1095 Q 1124 193 1142 171 L 1193 111 Q 1210 91 1238 91 H 1658 V 630 Z"
            fill="url(#faq-hero-soft)"
          />
          <path
            d="M 1138 630 L 1513 111 Q 1528 91 1551 91 H 1658 V 630 Z"
            fill="url(#faq-hero-bright)"
          />
        </svg>
        <div className="faq-hero-grid mx-auto w-[90%] max-w-[1440px]">
          <div className="faq-intro">
            <span className="faq-eyebrow">
              Frequently Asked Questions <CornerArrow />
            </span>
            <h1>
              Clear answers
              <span className="faq-heading-line">
                <span className="faq-heading-for">for</span>{' '}
                <span className="faq-heading-highlight">smarter shipping.</span>
              </span>
            </h1>
            <p className="faq-hero-description">
              Find quick answers about freight services, equipment, capacity,
              tracking, warehousing, carriers, and working with Ship Smart
              Solutions.
            </p>
          </div>
          <div className="faq-search-area">
            <form
              className="faq-search"
              onSubmit={(event) => {
                event.preventDefault();
                scrollToCategory('faq-results');
              }}
            >
              <Input
                aria-label="Search freight questions"
                placeholder="Search freight questions..."
                type="search"
                className="faq-search-input"
                prefix={<PiMagnifyingGlass aria-hidden="true" />}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
              <Button type="submit" className="faq-search-button">
                Search FAQs <span aria-hidden="true">→</span>
              </Button>
            </form>
            <h2 className="faq-topic-label">Browse by topic</h2>
            <div className="faq-topic-pills">
              {TOPIC_CARDS.map((topic, index) => {
                const Icon = topicIcons[index];
                return (
                  <Button
                    key={topic.number}
                    variant="outline"
                    className="faq-topic-pill"
                    onClick={() => {
                      setSearchQuery('');
                      requestAnimationFrame(() =>
                        scrollToCategory(topic.categoryId)
                      );
                    }}
                  >
                    <Icon aria-hidden="true" />
                    {topic.title}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      {!query && (
        <section
          className="faq-topic-overview mx-auto w-[90%] max-w-[1440px]"
          aria-label="Browse by topic"
        >
          {TOPIC_CARDS.map((topic) => (
            <article key={topic.number} className="faq-topic-summary">
              <div className="faq-topic-photo">
                <Image
                  src={topic.image}
                  alt={topic.title}
                  fill
                  sizes="(max-width: 767px) 50vw, 25vw"
                />
              </div>
              <h3>{topic.title}</h3>
              <p>{topic.description}</p>
            </article>
          ))}
        </section>
      )}
      <div id="faq-results" className="faq-results" aria-live="polite">
        {filteredCategories.length === 0 && (
          <div className="faq-empty mx-auto w-[90%] max-w-[1440px]">
            <p>No matching FAQs found.</p>
            <Button variant="outline" onClick={() => setSearchQuery('')}>
              Clear search
            </Button>
          </div>
        )}
        <div className="faq-stack">
          {filteredCategories.map((category) => (
            <section
              key={category.number}
              id={`category-${category.number}`}
              className="faq-category faq-ribbons"
            >
              <svg
                className="faq-category-art"
                viewBox="0 0 1668 940"
                preserveAspectRatio="none"
                aria-hidden="true"
                focusable="false"
              >
                <defs>
                  <linearGradient
                    id={`faq-category-soft-${category.number}`}
                    x1="0"
                    x2="1"
                    y1="1"
                    y2="0"
                  >
                    <stop stopColor="#e8f6ff" stopOpacity="0.55" />
                    <stop offset="0.62" stopColor="#c9e8ff" stopOpacity="0.84" />
                    <stop offset="1" stopColor="#a9d8ff" stopOpacity="0.95" />
                  </linearGradient>
                  <linearGradient
                    id={`faq-category-bright-${category.number}`}
                    x1="0"
                    x2="1"
                    y1="1"
                    y2="0"
                  >
                    <stop stopColor="#d4edff" stopOpacity="0.4" />
                    <stop offset="1" stopColor="#53b0fa" stopOpacity="0.84" />
                  </linearGradient>
                </defs>
                <path
                  d="M 1200 0 H 1668 V 940 H 242 L 284 894 Q 317 856 361 856 H 393 Q 428 856 451 829 L 897 210 Q 922 175 966 175 H 1030 Q 1068 175 1091 145 L 1160 48 Q 1193 0 1200 0 Z"
                  fill={`url(#faq-category-soft-${category.number})`}
                />
                <path
                  d="M 1504 0 H 1668 V 940 H 742 L 1360 175 Z"
                  fill={`url(#faq-category-bright-${category.number})`}
                />
              </svg>
              <div className="faq-category-grid mx-auto w-[90%] max-w-[1440px]">
                <div className="faq-category-intro">
                  <span className="faq-category-number" aria-hidden="true">
                    {category.number}
                  </span>
                  <h2>{category.title}</h2>
                  <p>{category.description}</p>
                </div>
                <div className="faq-questions">
                  {category.items.map((item) => {
                    const number = category.items.indexOf(item);
                    const originalIndex = FAQ_CATEGORIES.find(
                      (cat) => cat.number === category.number
                    )!.items.findIndex((faq) => faq.id === item.id);
                    return (
                      <FaqQuestion
                        key={`${item.id}-${query}`}
                        item={item}
                        number={originalIndex + 1}
                        defaultOpen={Boolean(query) || number === 0}
                      />
                    );
                  })}
                </div>
              </div>
            </section>
          ))}
        </div>
        <FaqClosingCta locale={locale} />
      </div>
    </SubPageShell>
  );
}
